import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from '@/components/landing/LandingLayout';
import { PainAwareLandingHero } from '@/components/landing/PainAwareLandingHero';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { BottomCtaBlock } from '@/components/landing/BottomCtaBlock';
import { AiSmartAutomationSection } from '@/components/landing/AiSmartAutomationSection';
import { FaqSection } from '@/components/landing/FaqSection';
import { BeforeAfterScenario } from '@/components/landing/BeforeAfterScenario';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { TrustBadges } from '@/components/landing/TrustBadges';
import { StatsSection } from '@/components/landing/StatsSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { RoiSection } from '@/components/landing/RoiSection';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { Card } from '@/components/ui/card';
import { Check, Calendar, Zap, Shield, Users } from 'lucide-react';

const TurnoverBnBAlternative = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const canonicalUrl = `https://rental.shift-box.com/${currentLang}/turnoverbnb-alternative`;
  const alternateLanguages = ['en', 'es', 'de', 'fr', 'it'];

  return (
    <>
      <Helmet>
        <title>{t('turnoverAlternative.seo.title')}</title>
        <meta name="description" content={t('turnoverAlternative.seo.description')} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags for multi-language support */}
        {alternateLanguages.map(lang => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`https://rental.shift-box.com/${lang}/turnoverbnb-alternative`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://rental.shift-box.com/en/turnoverbnb-alternative"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "Rental Manager - TurnoverBnB Alternative",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web"
              },
              {
                "@type": "FAQPage",
                "mainEntity": (t('turnoverAlternative.seo.faq', { returnObjects: true }) as any[]).map((faq: any) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }
            ]
          })}
        </script>
      </Helmet>

      <LandingLayout>
        <StickyFloatingCTA />
        
        {/* Hero */}
        <PainAwareLandingHero
          h1={t('turnoverAlternative.hero.h1')}
          subtitle={t('turnoverAlternative.hero.subtitle')}
          ctaText={t('turnoverAlternative.hero.ctaText')}
          ctaAction={() => navigate('/auth')}
          visualContent={
            <div className="relative animate-fade-in">
              <img 
                src="/screenshots/properties.webp" 
                alt={t('turnoverAlternative.hero.imageAlt')}
                className="rounded-lg shadow-2xl border border-border hover-scale transition-all duration-300"
              />
              <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-2xl animate-scale-in">
                <p className="text-sm font-bold">{t('turnoverAlternative.hero.badge')}</p>
              </div>
            </div>
          }
        />

        {/* Trust Badges */}
        <TrustBadges />

        {/* ROI Section */}
        <RoiSection />

        {/* Stats Section */}
        <StatsSection />

        {/* Unique focus paragraph */}
        <section className="py-12 container mx-auto px-4 max-w-4xl">
          <div className="text-center animate-fade-in">
            <h2 className="text-2xl font-bold mb-4">
              {t('turnoverAlternative.focusTitle')}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t('turnoverAlternative.focusText')}
            </p>
          </div>
        </section>

        {/* Positioning paragraph */}
        <section className="py-16 container mx-auto px-4 max-w-4xl">
          {((t('turnoverAlternative.positioning', { returnObjects: true }) as any) as string[]).map((paragraph, index) => (
            <p key={index} className={`text-lg ${index === 0 ? 'text-muted-foreground mb-6' : 'font-medium mb-8'}`}>
              {paragraph}
            </p>
          ))}
        </section>

        {/* Comparison table */}
        <ComparisonTable 
          title={t('turnoverAlternative.comparison.title')}
          rows={(t('turnoverAlternative.comparison.rows', { returnObjects: true }) as any[]).map(row => ({
            feature: row.feature,
            manual: row.manual,
            spreadsheet: row.spreadsheet,
            rentalManager: row.rentalManager
          }))}
        />

        {/* Why Choose Us Section */}
        <section className="py-16 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold mb-12 text-center">
              {t('turnoverAlternative.whyChoose.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {(t('turnoverAlternative.whyChoose.items', { returnObjects: true }) as any[]).map((item, index) => {
                const icons = [Calendar, Zap, Shield, Users];
                const Icon = icons[index];
                return (
                  <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-start gap-4">
                      <div className="rounded-full bg-primary/10 p-3">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Product Screenshots */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center">
              {t('turnoverAlternative.screenshots.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {(t('turnoverAlternative.screenshots.items', { returnObjects: true }) as any[]).map((item, index) => (
                <div key={index} className="space-y-4 animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <img 
                    src={item.image}
                    alt={item.alt}
                    className="rounded-lg shadow-xl border border-border hover-scale transition-all duration-300"
                  />
                  <p className="text-center text-sm text-muted-foreground font-medium">
                    {item.caption}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI/Smart Automation */}
        <AiSmartAutomationSection 
          title={t('turnoverAlternative.automation.title')}
          features={t('turnoverAlternative.automation.features', { returnObjects: true }) as string[]}
        />

        {/* Before/After Scenario */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('turnoverAlternative.beforeAfter.title')}
            </h2>
            <BeforeAfterScenario
              beforeText={t('turnoverAlternative.beforeAfter.before')}
              afterText={t('turnoverAlternative.beforeAfter.after')}
            />
          </div>
        </section>

        {/* Value Props */}
        <ValuePropsSection />

        {/* Testimonials */}
        <section className="py-16 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4 text-center">
              {t('turnoverAlternative.testimonials.title')}
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('turnoverAlternative.testimonials.subtitle')}
            </p>
            
            <TestimonialCarousel 
              testimonials={t('turnoverAlternative.testimonials.items', { returnObjects: true }) as any[]}
            />
          </div>
        </section>

        {/* FAQ */}
        <FaqSection faqs={t('turnoverAlternative.faq', { returnObjects: true }) as any[]} />

        {/* AI Assistant Summary */}
        <AiAssistantSummary />

        {/* Final CTA Section */}
        <FinalCTASection />

        {/* Bottom CTA */}
        <BottomCtaBlock />
      </LandingLayout>
    </>
  );
};

export default TurnoverBnBAlternative;
